/**
 * Wilf's book — the offline trade round (order 539; rules in tradeModel.ts, numbers in tradeData.ts, docs in
 * docs/TRADE-OFFLINE.md). Opens once Wet Paper is done (Wilf is in the story by then): a note from Wilf, a
 * WILF'S BOOK tile in the book, and from then on:
 *
 *  - a trade card at every door and buyer (E / USE near the place, on foot or stopped in a car): buy at the
 *    four doors, sell to the eight buyers; at Lockup 7 (once you have the key) the card is your stash;
 *  - Lenny's tips every few minutes of play, posted to the book and marked on the map, each with a clock;
 *  - the Vanes' runner (runner.ts) when Lenny says it's out: pull the driver out at a stop, get the van to a
 *    lockup with nobody after you, open the panels;
 *  - consequences: nicked = your pockets and the boot of the car you were in are gone; laid out = your pockets;
 *    a loaded car wrecked or lost = its boot. A big sale can bring the law (`dealRisk`).
 *
 * Offline only: online the server owns money and goods (the markets are the online version of this), so the
 * system is inert there — no interactions, no tips, no tile.
 */
import type { GameContext } from '../core/context';
import { distance, type V2 } from '../core/math';
import type { System, SystemFactory } from '../core/system';
import type { BookEntry, BookPage } from '../book/bookSystem';
import { touchUi } from '../input/touch';
import { WET_PAPER } from '../missions/ids';
import { LOCKUP_UNLOCK_ID, formatMoney } from '../economy/wallet';
import { PLAYER_LOCKUP_ID } from '../economy/playerLockup';
import type { MapMarker } from '../ui/uiTypes';
import type { Vehicle } from '../vehicles/types';
import { vehicleCapacity } from '../../shared/economy';
import { Runner, RUNNER_TAG } from './runner';
import { TradeCard, type TradeCardInfo, type TradeCardModel, type TradeCardRow } from './tradeCard';
import {
  GOOD_LABEL,
  HOLDALL_PRICE,
  STASH_CAPACITY,
  TRADE,
  TRADE_GOODS,
  TRADE_HOLDALL_UNLOCK,
  TRADE_PLACES,
  goodsText,
  tradePlace,
  type Goods,
  type TradeGood,
  type TradePlace,
} from './tradeData';
import { RISK_TEXT, Trader, count, isEmpty, rollRunnerLoad, units, type CarRef, type Tip } from './tradeModel';

declare module '../core/events' {
  interface GameEvents {
    /** A trade (offline, Wilf's book): for the contact ladders (WP-X1) and the record. `profit` is 0 on a purchase. */
    tradeDeal: { place: string; good: string; qty: number; total: number; profit: number; kind: 'buy' | 'sell' };
    /** The Vanes' runner was opened at a lockup: what was in it (empty sometimes). */
    runnerOpened: { goods: Record<string, number>; empty: boolean };
  }
}

declare module '../core/hooks' {
  interface BhHooks {
    /** Wilf's book read-out: open, the clock, holdings, tips, the card on screen, the runner, the places. */
    tradeState(): Record<string, unknown>;
    /** Press a button on the trade card on screen (row id, button id); false if there is no such button. */
    tradePress(row: string, button: string): boolean;
    /** Force Lenny's next tip now ('buyer' | 'lot' | 'runner'); returns the tip or null. */
    tradeTip(kind?: string): Record<string, unknown> | null;
    /** Put goods in your pockets (tests): good id, quantity, average price paid. */
    tradeGive(good: string, qty: number, paid?: number): boolean;
  }
}

/** Reach of a trade place's prompt (m), on foot or stopped in a car. */
const REACH = 5.5;
/** A street buyer by a phone box: this far along the pavement from the box, and a smaller reach. */
const BOX_OFFSET = 5;
const BOX_REACH = 3.4;
/** A lockup's reach for opening the Vanes' van. */
const LOCKUP_REACH = 9;
/** Buyers show on the map this close; doors always. */
const BUYER_MAP_RANGE = 120;
const STASH_ID = 'stash';

const pounds = (n: number): string => `£${formatMoney(Math.round(n))}`;
const clock = (s: number): string => `${Math.floor(Math.max(0, s) / 60)}:${String(Math.floor(Math.max(0, s) % 60)).padStart(2, '0')}`;
const unitOf = (g: TradeGood): string => GOOD_LABEL[g].unit;

export function createTradeSystemFactories(): [SystemFactory, SystemFactory] {
  /** The van on its round (shared by the driver system and the rules). */
  let runner: Runner | null = null;

  const driverSystem: SystemFactory = () => ({
    id: 'trade-runner',
    order: 268,
    fixedUpdate(dt) {
      runner?.drive(dt);
    },
  });

  const tradeSystem: SystemFactory = (): System => {
    let ctx: GameContext;
    const trader = new Trader();
    const pos = new Map<string, V2>();
    let stashPos: V2 | null = null;
    const lockups: { id: string; name: string; pos: V2 }[] = [];
    const bootNames = new Map<number, string>();
    let card: { c: TradeCard; place: string } | null = null;
    let say = '';
    let sayBad = false;
    let frozeTime = false;
    let check = 0;
    let lostSeq = 0;
    const offs: (() => void)[] = [];
    let rng: ReturnType<GameContext['rng']['fork']>;

    const save = () => ctx.services.get('save');
    const ui = () => ctx.services.tryGet('ui');
    const book = () => ctx.services.tryGet('book');
    /** A message in the book (not headless: the book's delivery runs on the wall clock). */
    const post = (m: Parameters<NonNullable<ReturnType<typeof book>>['post']>[0]): void => {
      if (typeof window !== 'undefined') book()?.post(m);
    };
    const audio = () => ctx.services.tryGet('audio');
    const offline = (): boolean => !ctx.services.tryGet('net')?.online && (ctx.services.tryGet('economy')?.authority ?? 'local') === 'local' && !save().suspended;
    const open = (): boolean => offline() && save().isMissionComplete(WET_PAPER);
    const now = (): number => save().data.stats.playTime;
    const cash = (): number => ctx.services.tryGet('economy')?.balance ?? ctx.state.money;
    const holdall = (): boolean => trader.s.holdall || save().hasUnlock(TRADE_HOLDALL_UNLOCK);
    const hasStash = (): boolean => save().hasUnlock(LOCKUP_UNLOCK_ID);

    function carRef(v: Vehicle | null | undefined): CarRef | null {
      if (!v || v.destroyed || v.isPolice) return null;
      bootNames.set(v.id, v.spec.displayName);
      return { id: v.id, name: v.spec.displayName, capacity: vehicleCapacity(v.spec.id) };
    }
    /** The car you're in, or (on foot) the one you just got out of if it's right beside you. */
    function myCar(): CarRef | null {
      const player = ctx.services.get('player');
      return player.mode === 'driving' ? carRef(player.vehicle) : null;
    }

    function persist(): void {
      save().data.trade = trader.snapshot();
      save().markDirty();
    }

    function load(): void {
      trader.load(save().data.trade, hasStash());
      bootNames.clear();
      persist();
    }

    /** Why the places won't deal right now (null = they will). */
    function refusal(): string | null {
      if (ctx.state.heat > 0) return 'not with the law after you';
      if (ctx.state.activeMissionId !== null) return 'not while you’re on a job';
      return null;
    }

    // ------------------------------------------------------------------ the card

    function cardInfo(placeId: string): TradeCardInfo {
      const car = myCar();
      const t = now();
      const used = trader.capUsed(t);
      const carry = `Pockets ${units(trader.s.pockets)} of ${trader.pocketCap(holdall())}${car ? ` · Boot of the ${car.name} ${units(trader.boot(car)!)} of ${car.capacity}` : ''}${placeId === STASH_ID ? ` · Stash ${units(trader.s.stash)} of ${STASH_CAPACITY}` : ''}`;
      const full = used >= TRADE.hourCap;
      const limit = full ? `This hour’s limit reached (${pounds(TRADE.hourCap)} profit): buyers pay a quarter of the margin for ${clock(trader.capFreesIn(t))}` : `This hour: ${pounds(used)} of ${pounds(TRADE.hourCap)} profit`;
      const tip = trader.liveTips(t).find((x) => x.place === placeId);
      return { carry, limit, tip: tip ? tipLine(tip, t) : null, say, bad: sayBad };
    }

    function tipLine(tip: Tip, t: number, where = 'here'): string {
      const g = tip.good ? GOOD_LABEL[tip.good].name.toLowerCase() : '';
      if (tip.kind === 'buyer') return `Lenny’s tip: ${Math.round((tip.mul - 1) * 100)} % over the odds for ${g} ${where} — ${clock(tip.until - t)} left`;
      if (tip.kind === 'lot') return `Lenny’s tip: ${tip.qty - tip.used} extra ${GOOD_LABEL[tip.good!].units} of ${g} at about a third off — ${clock(tip.until - t)} left`;
      return `Lenny’s tip: the Vanes’ runner is out — ${clock(tip.until - t)} left`;
    }

    function rowsFor(placeId: string): TradeCardRow[] {
      const t = now();
      const car = myCar();
      const rows: TradeCardRow[] = [];
      if (placeId === STASH_ID) return stashRows(car);
      const p = tradePlace(placeId);
      if (!p) return rows;
      if (p.door) {
        for (const g of Object.keys(p.door) as TradeGood[]) {
          const q = trader.buyQuote(p.id, g, 1, t);
          const left = trader.doorLeft(p.id, g, t);
          const max = trader.maxBuy(p.id, g, t, car, holdall(), cash());
          const room = trader.room(g, car, holdall());
          const back = left === 0 ? ` · more in ${clock(trader.doorBackIn(p.id, t))}` : '';
          const note = left === 0 ? `None left${back}` : room === 0 ? (car ? `${left} left · no room in the boot` : `${left} left · you can’t carry more on foot`) : `${left} left · room for ${room}${max < Math.min(left, room) ? ` · cash for ${max}` : ''}`;
          rows.push({
            id: `buy:${g}`,
            section: 'Buy here',
            label: GOOD_LABEL[g].name,
            value: `${pounds(q.unit)} a ${unitOf(g)}`,
            note,
            ...(left === 0 || room === 0 ? { tone: 'warn' as const } : {}),
            buttons: [
              { id: 'one', label: 'BUY 1', enabled: max >= 1 },
              { id: 'max', label: max > 1 ? `BUY ${max}` : 'BUY MAX', enabled: max > 1 },
            ],
          });
        }
      }
      if (p.buyer) {
        for (const g of p.buyer.accepts) {
          const have = trader.carried(g, car);
          const max = trader.maxSell(p.id, g, car);
          const one = trader.sellQuote(p.id, g, 1, t);
          const all = max > 0 ? trader.sellQuote(p.id, g, max, t) : null;
          const paid = trader.s.paid[g];
          let note = 'You have none with you.';
          let tone: TradeCardRow['tone'];
          if (have > 0 && all) {
            const each = paid !== undefined ? one.unit - paid : 0;
            tone = paid === undefined || each >= 0 ? 'up' : 'down';
            const vs = paid !== undefined ? `paid ${pounds(paid)}: ${each >= 0 ? '▲' : '▼'} ${pounds(Math.abs(each))} a ${unitOf(g)}` : 'paid nothing';
            note = `${have} with you, ${vs} · ${max > 1 ? `${max} at once: ` : ''}${RISK_TEXT[all.risk.label]}`;
            if (all.capped > 0) note += ` (−${pounds(all.capped)} this hour)`;
          }
          rows.push({
            id: `sell:${g}`,
            section: 'Sell here',
            label: GOOD_LABEL[g].name,
            value: `${pounds(one.unit)} a ${unitOf(g)}`,
            note,
            ...(tone ? { tone } : {}),
            buttons: [
              { id: 'one', label: 'SELL 1', enabled: max >= 1 },
              { id: 'all', label: max > 1 ? `SELL ${max}` : 'SELL ALL', enabled: max > 1 },
            ],
          });
        }
      }
      if (p.wilf) {
        const owned = holdall();
        rows.push({
          id: 'holdall',
          section: 'Wilf',
          label: 'Canvas holdall',
          value: owned ? 'Yours' : pounds(HOLDALL_PRICE),
          note: owned ? 'Room for twelve on foot instead of six.' : 'Room for six more on foot (twelve, not six). Yours for good.',
          buttons: [{ id: 'buy', label: owned ? 'GOT IT' : 'BUY', enabled: !owned && cash() >= HOLDALL_PRICE }],
        });
      }
      return rows;
    }

    function stashRows(car: CarRef | null): TradeCardRow[] {
      const carrying = !isEmpty(trader.s.pockets) || (car ? !isEmpty(trader.boot(car)!) : false);
      const rows: TradeCardRow[] = [
        {
          id: 'all',
          section: 'Your stash, Lockup 7',
          label: 'Put it all away',
          value: `${units(trader.s.stash)} of ${STASH_CAPACITY}`,
          note: carrying ? `Everything in your pockets${car ? ' and the boot' : ''}. The law never finds it here.` : 'You aren’t carrying anything.',
          buttons: [{ id: 'in', label: 'PUT AWAY', enabled: carrying && units(trader.s.stash) < STASH_CAPACITY }],
        },
      ];
      for (const g of TRADE_GOODS) {
        const inStash = trader.s.stash[g] ?? 0;
        const withYou = trader.carried(g, car);
        if (!inStash && !withYou) continue;
        const room = trader.room(g, car, holdall());
        rows.push({
          id: `take:${g}`,
          section: 'In the stash',
          label: GOOD_LABEL[g].name,
          value: `${inStash} here`,
          note: `${withYou} with you${trader.s.paid[g] !== undefined ? ` · paid ${pounds(trader.s.paid[g]!)} a ${unitOf(g)}` : ''}${inStash && !room ? (car ? ' · no room in the boot' : ' · no room on foot') : ''}`,
          buttons: [
            { id: 'one', label: 'TAKE 1', enabled: inStash > 0 && room > 0 },
            { id: 'all', label: 'TAKE ALL', enabled: inStash > 1 && room > 1 },
          ],
        });
      }
      return rows;
    }

    function said(text: string, bad = false): void {
      say = text;
      sayBad = bad;
    }

    function press(placeId: string, row: string, button: string): boolean {
      const t = now();
      const car = myCar();
      const [kind, gid] = row.split(':') as [string, TradeGood | undefined];
      const econ = ctx.services.tryGet('economy');
      if (placeId === STASH_ID) {
        if (row === 'all') {
          const moved = trader.stashAll(car);
          persist();
          if (isEmpty(moved)) return said('No room left in the stash.', true), false;
          said(`Put away: ${listGoods(moved)}.`);
          return true;
        }
        if (kind === 'take' && gid) {
          const n = trader.takeOut(gid, button === 'all' ? 999 : 1, car, holdall());
          persist();
          if (!n) return said(car ? 'No room in the boot.' : 'No room on foot.', true), false;
          said(`Took ${goodsText(gid, n)}${car ? ' to the boot' : ''}.`);
          return true;
        }
        return false;
      }
      const p = tradePlace(placeId);
      if (!p) return false;
      if (row === 'holdall') {
        if (holdall()) return false;
        if (!econ?.spend(HOLDALL_PRICE, 'trade:holdall')) return said('Not enough cash.', true), false;
        trader.s.holdall = true;
        persist();
        audio()?.ui('cash');
        said('Bought the holdall: twelve on foot from now on.');
        return true;
      }
      if (!gid) return false;
      const why = refusal();
      if (why) return said(`They won’t deal — ${why}.`, true), false;
      if (kind === 'buy') {
        const qty = button === 'max' ? trader.maxBuy(p.id, gid, t, car, holdall(), cash()) : 1;
        const q = trader.buy(p.id, gid, qty, t, car, holdall(), cash());
        if (!q.ok) return said(q.reason ?? 'No.', true), false;
        econ?.spend(q.total, `trade:buy:${gid}`);
        persist();
        audio()?.ui('cash');
        said(`Bought ${goodsText(gid, qty)} for ${pounds(q.total)}${car ? ` — in the boot of the ${car.name}` : ''}.`);
        ctx.events.emit('tradeDeal', { place: p.id, good: gid, qty, total: q.total, profit: 0, kind: 'buy' });
        return true;
      }
      if (kind === 'sell') {
        const qty = button === 'all' ? trader.maxSell(p.id, gid, car) : 1;
        const q = trader.sell(p.id, gid, qty, t, car);
        if (!q.ok) return said(q.reason ?? 'No.', true), false;
        econ?.earn(q.total, `trade:sell:${gid}`);
        persist();
        audio()?.ui('cash');
        said(`Sold ${goodsText(gid, qty)} for ${pounds(q.total)} (${q.profit >= 0 ? '▲' : '▼'} ${pounds(Math.abs(q.profit))}).`);
        ctx.events.emit('tradeDeal', { place: p.id, good: gid, qty, total: q.total, profit: q.profit, kind: 'sell' });
        if (q.risk.chance > 0 && rng.chance(q.risk.chance)) clocked(q.risk.level);
        return true;
      }
      return false;
    }

    /** The deal was seen: the card shuts and the law comes. */
    function clocked(level: number): void {
      closeCard();
      const heat = ctx.services.tryGet('heat');
      if (heat && heat.level < level) heat.setLevel(level);
      ui()?.toast('Somebody saw that deal — the law’s coming. Get away and lose them.', 'bad', 4);
    }

    function openCard(placeId: string): boolean {
      const u = ui();
      if (!u || !ctx.uiRoot || card) return false;
      const root = ctx.uiRoot.querySelector('.bh-ui') as HTMLElement | null;
      if (!root) return false;
      const p = tradePlace(placeId);
      if (p) {
        trader.hear(p.id, now());
        persist();
      }
      say = p && p.door && !p.buyer ? `${p.who}: “What’ll it be?”` : p ? `${p.who} has a look at what you’ve got.` : 'Lockup 7: the shutter rattles up.';
      sayBad = false;
      const model: TradeCardModel = {
        title: p ? p.name : 'Lockup 7',
        sub: p ? [p.door ? p.who : '', p.buyer ? (p.buyer.type === 'fence' ? 'A fence: takes a lot, pays more, draws more attention' : 'A street buyer: three at a time, barely noticed') : ''].filter(Boolean).join(' · ') : 'Your stash: the law never finds it here',
        balance: cash,
        info: () => cardInfo(placeId),
        rows: () => rowsFor(placeId),
        press: (r, b) => press(placeId, r, b),
        close: closeCard,
        touch: () => touchUi.enabled,
        sound: (s) => audio()?.ui(s),
      };
      const c = new TradeCard(root, model);
      if (!u.openOverlay(c, 'trade', () => teardown(c))) {
        c.dispose();
        return false;
      }
      card = { c, place: placeId };
      ctx.uiRoot.classList.add('tr-open');
      audio()?.ui('whoosh');
      if (ctx.loop.timeScale > 0) {
        frozeTime = true;
        ctx.loop.timeScale = 0;
      }
      ctx.services.get('player').controls.acquire('trade');
      ctx.input.clearTouch?.();
      return true;
    }

    function teardown(c: TradeCard): void {
      if (card?.c !== c) return;
      card = null;
      c.dispose();
      ctx.uiRoot?.classList.remove('tr-open');
      if (frozeTime) {
        frozeTime = false;
        if (ctx.loop.timeScale === 0) ctx.loop.timeScale = 1;
      }
      ctx.services.get('player').controls.release('trade');
    }

    function closeCard(): void {
      const c = card?.c;
      if (!c) return;
      ui()?.closeOverlay(c);
      teardown(c);
    }

    // ------------------------------------------------------------------ losses

    function listGoods(g: Goods): string {
      return TRADE_GOODS.filter((k) => g[k]).map((k) => goodsText(k, g[k]!)).join(', ');
    }

    function reportLoss(lost: Goods, subject: string, how: string): void {
      if (isEmpty(lost)) return;
      persist();
      post({ key: `trade-lost-${now().toFixed(0)}-${++lostSeq}`, from: 'Wilf Tuckett', kind: 'warning', subject, text: `${how} ${listGoods(lost)}. Gone. Keep the big loads in the lockup, son.` });
    }

    /** Boots of cars that are no longer in the world (wrecked, towed, streamed out). */
    function checkBoots(): void {
      const vehicles = ctx.services.get('vehicles');
      for (const id of [...trader.boots.keys()]) {
        const b = trader.boots.get(id)!;
        const v = vehicles.get(id);
        if (v && !v.destroyed) continue;
        if (isEmpty(b)) {
          trader.boots.delete(id);
          continue;
        }
        const name = bootNames.get(id) ?? 'motor';
        const lost = trader.loseBoot(id);
        ui()?.toast(`The ${name} is gone, and the goods in its boot with it.`, 'bad', 3.5);
        reportLoss(lost, `Lost with the ${name}`, `The ${name} went, and with it:`);
      }
    }

    // ------------------------------------------------------------------ Lenny's tips & the runner

    function postTip(tip: Tip): void {
      const p = tradePlace(tip.place);
      const at = tip.kind === 'runner' ? (runner?.van.pos ?? null) : p ? (pos.get(p.id) ?? null) : null;
      const g = tip.good ? GOOD_LABEL[tip.good] : null;
      let subject = '';
      let text = '';
      if (tip.kind === 'buyer' && p && g) {
        subject = `${g.name} wanted: ${p.name}`;
        text = `${p.name} is paying over the odds for ${g.name.toLowerCase()} — about ${Math.round((tip.mul - 1) * 100)} % more than usual. Good for ten minutes, so don’t hang about.\n\nIt’s in Wilf’s book with the time left.`;
      } else if (tip.kind === 'lot' && p && g) {
        subject = `A cheap lot: ${p.name}`;
        text = `${p.who} at ${p.name} has an extra ${tip.qty} ${g.units} of ${g.name.toLowerCase()} going for about a third off. Eight minutes, then it’s gone.\n\nIt’s in Wilf’s book with the time left.`;
      } else {
        subject = 'The Vanes’ runner is out';
        text = 'The Vanes’ van — a claret Haulmaster — is doing its round now: The Velvet Lamp, the Brickmakers Arms on Kiln Street, Bonded No. 3. It stops a few seconds at each door. Pull the driver out at a stop, get the van to a lockup with nobody after you, and open it up. Five minutes.\n\nSometimes it’s empty. Sometimes it isn’t.';
      }
      post({ key: `trade-${tip.id}`, from: 'Lenny the Ear', kind: 'tip', subject, text, ...(at ? { action: { label: 'Mark on the map', mark: { x: at.x, z: at.z, label: tip.kind === 'runner' ? 'The Vanes’ runner' : p!.name } } } : {}) });
    }

    function tickTips(): void {
      const t = now();
      const canRun = runner === null && ctx.state.activeMissionId === null;
      const tip = trader.nextTip(t, rng, canRun);
      if (!tip) return;
      if (tip.kind === 'runner' && !startRunner()) {
        trader.s.tips = trader.s.tips.filter((x) => x !== tip);
        return;
      }
      persist();
      postTip(tip);
    }

    function startRunner(): boolean {
      runner = Runner.spawn(ctx);
      return runner !== null;
    }

    function endRunner(): void {
      const r = runner;
      runner = null;
      trader.s.tips = trader.s.tips.filter((x) => x.kind !== 'runner');
      persist();
      if (!r) return;
      const v = ctx.services.get('vehicles').get(r.van.id);
      if (v) v.tags.delete(RUNNER_TAG);
    }

    function tickRunner(): void {
      const r = runner;
      if (!r) return;
      const vehicles = ctx.services.get('vehicles');
      const player = ctx.services.get('player');
      const v = vehicles.get(r.van.id);
      if (!v || v.destroyed) {
        if (r.hijacked) ui()?.toast('The Vanes’ van is gone, and whatever was in it.', 'bad', 3.5);
        endRunner();
        return;
      }
      if (!r.hijacked && player.vehicle === v && player.mode === 'driving') {
        r.hijacked = true;
        trader.s.tips = trader.s.tips.filter((x) => x.kind !== 'runner');
        persist();
        ui()?.say('You', 'Right. A lockup, quiet, and nobody on my tail.', 4);
        ui()?.toast('You’ve got the Vanes’ van. Get it to a lockup (the lockup on the map) with nobody after you, then open it up.', 'info', 6);
        return;
      }
      // The tip ran out with the van still theirs: off the road once you can't see it.
      if (!r.hijacked && !trader.liveTips(now()).some((x) => x.kind === 'runner') && r.unseen()) {
        vehicles.despawn(v);
        runner = null;
      }
    }

    function openRunner(lockup: { name: string }): void {
      const r = runner;
      const car = myCar();
      if (!r || !car) return;
      const load = rollRunnerLoad(rng, car.capacity);
      const boot = trader.boot(car)!;
      for (const g of TRADE_GOODS) if (load[g]) trader.addHeld(g, load[g]!, 0, boot);
      trader.s.stats.runners++;
      const empty = isEmpty(load);
      endRunner();
      audio()?.ui(empty ? 'denied' : 'stamp');
      if (empty) ui()?.toast(`${lockup.name}: you open the panels — empty. They’d dropped the lot already.`, 'warn', 5);
      else ui()?.toast(`${lockup.name}: you open the panels — ${listGoods(load)}. It’s in the boot; sell it, or stash it at Lockup 7.`, 'good', 6);
      ctx.events.emit('runnerOpened', { goods: { ...load } as Record<string, number>, empty });
    }

    // ------------------------------------------------------------------ the book

    function placeMark(p: TradePlace): BookEntry['actions'] {
      const at = pos.get(p.id);
      return at ? [{ label: 'Mark on the map', sub: p.name, run: () => markOnMap(at, p.name) }] : [];
    }

    function markOnMap(at: V2, label: string): void {
      book()?.act({ label: 'Mark on the map', mark: { x: at.x, z: at.z, label } });
    }

    function bookPage(): BookPage {
      const t = now();
      const car = myCar();
      const entries: BookEntry[] = [];
      const tips = trader.liveTips(t);
      for (const tip of tips) {
        const p = tradePlace(tip.place);
        const at = tip.kind === 'runner' ? (runner?.van.pos ?? null) : p ? (pos.get(p.id) ?? null) : null;
        entries.push({
          id: `trade:${tip.id}`,
          section: 'Lenny’s tips',
          title: tip.kind === 'runner' ? 'The Vanes’ runner is out' : tip.kind === 'buyer' ? `${GOOD_LABEL[tip.good!].name} wanted` : `Cheap ${GOOD_LABEL[tip.good!].name.toLowerCase()}`,
          sub: `${clock(tip.until - t)} left${p ? ` · ${p.name}` : ''}`,
          mark: 'new',
          avatar: 'Lenny the Ear',
          kicker: 'Lenny’s tip',
          heading: tipLine(tip, t, p ? `at ${p.name}` : 'there').replace(/^Lenny’s tip: /, ''),
          body:
            tip.kind === 'runner'
              ? ['A claret Haulmaster doing the Vanes’ round: The Velvet Lamp, the Brickmakers Arms, Bonded No. 3. It stops a few seconds at each door — pull the driver out then.', 'Get it to a lockup (the lockup on the map) with nobody after you and open it up. Sometimes it’s empty.']
              : tip.kind === 'buyer'
                ? [`${p?.name ?? 'A buyer'} pays about ${Math.round((tip.mul - 1) * 100)} % more than usual for ${GOOD_LABEL[tip.good!].name.toLowerCase()} until the time runs out.`]
                : [`${p?.who ?? 'They'} at ${p?.name ?? 'a door'} has ${tip.qty - tip.used} extra ${GOOD_LABEL[tip.good!].units} at about a third off.`],
          actions: at ? [{ label: 'Mark on the map', sub: tip.kind === 'runner' ? 'The Vanes’ runner' : p!.name, run: () => markOnMap(at, tip.kind === 'runner' ? 'The Vanes’ runner' : p!.name) }] : [],
        });
      }
      if (!tips.length) {
        entries.push({ id: 'trade:no-tips', section: 'Lenny’s tips', title: 'No tips right now', sub: 'Lenny rings with one every few minutes', heading: 'Lenny’s tips', body: ['A buyer paying over the odds, a cheap lot, or the Vanes’ runner on its round. Each one comes as a message, shows here with the time left, and goes when the time is up.'] });
      }
      // What you've got.
      const holdLines = (g: Goods): string[] => TRADE_GOODS.filter((k) => g[k]).map((k) => `${goodsText(k, g[k]!)}${trader.s.paid[k] !== undefined ? ` — paid ${pounds(trader.s.paid[k]!)} a ${unitOf(k)} on average` : ''}`);
      entries.push({
        id: 'trade:pockets',
        section: 'What you’ve got',
        title: 'On you',
        sub: `${units(trader.s.pockets)} of ${trader.pocketCap(holdall())}${isEmpty(trader.s.pockets) ? ' · nothing' : ''}`,
        heading: 'In your pockets',
        body: [...(isEmpty(trader.s.pockets) ? ['Nothing.'] : holdLines(trader.s.pockets)), `Room for ${trader.pocketCap(holdall())} on foot${holdall() ? ' (with the holdall)' : ` — Wilf’s holdall makes it ${trader.pocketCap(true)}`}. Lost if you’re nicked or laid out.`],
      });
      for (const [id, b] of trader.boots) {
        if (isEmpty(b)) continue;
        const v = ctx.services.get('vehicles').get(id);
        const name = bootNames.get(id) ?? 'motor';
        const here = car?.id === id;
        entries.push({
          id: `trade:boot:${id}`,
          section: 'What you’ve got',
          title: `In the boot of the ${name}`,
          sub: here ? 'the car you’re in' : v ? `${Math.round(distance(ctx.services.get('player').pos, v.pos))} m away` : 'gone',
          heading: `The boot of the ${name}`,
          body: [...holdLines(b), 'Lost if the car is wrecked or lost, or if you’re nicked in it.'],
          ...(v && !here ? { actions: [{ label: 'Mark on the map', sub: `the ${name}`, run: () => markOnMap(v.pos, `The ${name}`) }] } : {}),
        });
      }
      entries.push({
        id: 'trade:stash',
        section: 'What you’ve got',
        title: 'The stash, Lockup 7',
        sub: hasStash() ? `${units(trader.s.stash)} of ${STASH_CAPACITY}` : 'needs the lockup key',
        heading: 'Your stash',
        body: hasStash() ? [...(isEmpty(trader.s.stash) ? ['Empty.'] : holdLines(trader.s.stash)), 'Safe from the law. Drive or walk up to Lockup 7 to put goods away or take them out.'] : ['Lockup 7 on Pilcher Ginnel is the safe place for goods, once you have the key (Money in the book).'],
        ...(stashPos ? { actions: [{ label: 'Mark on the map', sub: 'Lockup 7, Pilcher Ginnel', run: () => markOnMap(stashPos!, 'Lockup 7') }] } : {}),
      });
      // Prices you've heard.
      for (const g of TRADE_GOODS) {
        let buy: { p: TradePlace; v: number; t: number } | null = null;
        let sell: { p: TradePlace; v: number; t: number } | null = null;
        for (const p of TRADE_PLACES) {
          const h = trader.s.heard[p.id];
          if (!h) continue;
          const b = h.buy[g];
          const s = h.sell[g];
          if (b !== undefined && (!buy || b < buy.v)) buy = { p, v: b, t: h.t };
          if (s !== undefined && (!sell || s > sell.v)) sell = { p, v: s, t: h.t };
        }
        const paid = trader.s.paid[g];
        const arrow = sell && paid !== undefined ? (sell.v >= paid ? ' ▲' : ' ▼') : '';
        const ago = (x: number): string => `${Math.max(0, Math.round((t - x) / 60))} min ago`;
        const sub = buy || sell ? [buy ? `buy ${pounds(buy.v)}` : '', sell ? `sell ${pounds(sell.v)}${arrow}` : ''].filter(Boolean).join(' · ') : 'not heard yet';
        entries.push({
          id: `trade:price:${g}`,
          section: 'Prices you’ve heard',
          title: GOOD_LABEL[g].name,
          sub,
          heading: `${GOOD_LABEL[g].name}, a ${unitOf(g)}`,
          body: [
            buy ? `Cheapest heard: ${pounds(buy.v)} at ${buy.p.name} (${ago(buy.t)}).` : `Sold at ${TRADE_PLACES.filter((p) => p.door?.[g]).map((p) => p.name).join(', ')} — go and ask.`,
            sell ? `Best heard: ${pounds(sell.v)} from ${sell.p.name} (${ago(sell.t)}).` : 'No buyer’s price heard yet. Open a buyer’s card to hear it.',
            ...(paid !== undefined ? [`You paid ${pounds(paid)} a ${unitOf(g)} on average.`] : []),
            'Prices move: a buyer pays less as the district fills up and as the law takes an interest; a door’s price rises as its lot goes.',
          ],
        });
      }
      // Where to deal.
      for (const p of TRADE_PLACES) {
        const what = [p.door ? `buys: sells you ${Object.keys(p.door).map((g) => GOOD_LABEL[g as TradeGood].name.toLowerCase()).join(', ')}` : '', p.buyer ? `${p.buyer.type === 'fence' ? 'fence' : 'street buyer'}: takes ${p.buyer.accepts.map((g) => GOOD_LABEL[g].name.toLowerCase()).join(', ')}` : ''].filter(Boolean);
        entries.push({
          id: `trade:place:${p.id}`,
          section: p.door ? 'Where to buy' : 'Where to sell',
          title: p.name,
          sub: p.door ? Object.keys(p.door).map((g) => GOOD_LABEL[g as TradeGood].name).join(', ') : p.buyer!.type === 'fence' ? 'a fence' : 'a street buyer',
          heading: p.name,
          body: [`${p.who} — ${what.join('; ')}.`.replace('buys: sells', 'sells'), ...(p.wilf ? ['Wilf also sells a canvas holdall: twelve on foot instead of six.'] : [])],
          actions: placeMark(p),
        });
      }
      const used = trader.capUsed(t);
      entries.push({
        id: 'trade:how',
        section: 'How it works',
        title: 'Buy low, sell high',
        sub: `this hour ${pounds(used)} of ${pounds(TRADE.hourCap)}`,
        heading: 'How Wilf’s book works',
        body: [
          'Buy at a door, sell across town where it fetches more. Walk or drive up to a door or a buyer and press E for their card. Each district wants different things: the prices you’ve heard are on this page.',
          `On foot you carry ${trader.pocketCap(false)} (bulky goods one at a time); a car’s boot holds far more — a van the most. Lockup 7 is the safe stash once you have the key.`,
          'Nicked: the law keeps what’s in your pockets and in the boot of the car you’re in. Laid out: your pockets. A car wrecked or lost takes its boot with it.',
          'Street buyers take three at a time and nobody notices. Fences take a lot and pay more, but a big sale gets seen: the card says the risk before you sell.',
          `The borough only stands so much: past ${pounds(TRADE.hourCap)} profit in an hour of play, buyers pay a quarter of the margin until it cools.`,
          'Lenny rings with tips every few minutes: a buyer paying over the odds, a cheap lot, or the Vanes’ runner on its round.',
          `So far: ${trader.s.stats.deals} sales, ${pounds(trader.s.stats.profit)} profit, best sale ${pounds(trader.s.stats.best)}, ${trader.s.stats.runners} of the Vanes’ vans opened, ${trader.s.stats.lost} lost to the law or the road.`,
        ].map((s) => (touchUi.enabled ? s.replace('press E', 'tap USE') : s)),
      });
      return { heading: 'Wilf’s book', lede: 'Buy low, sell high, keep the law out of it.', entries };
    }

    // ------------------------------------------------------------------ map

    function markers(out: MapMarker[]): void {
      if (!open() || (ctx.state.mode !== 'playing' && ctx.state.mode !== 'paused')) return;
      const t = now();
      const me = ctx.services.get('player').pos;
      const tipped = new Set<string>();
      for (const tip of trader.liveTips(t)) {
        if (tip.kind === 'runner') continue;
        const at = pos.get(tip.place);
        if (!at) continue;
        tipped.add(tip.place);
        out.push({ x: at.x, z: at.z, kind: 'event', glyph: '£', pulse: true, pin: true, label: `Lenny’s tip — ${tradePlace(tip.place)?.name ?? ''}` });
      }
      if (ctx.state.activeMissionId !== null) return;
      for (const p of TRADE_PLACES) {
        const at = pos.get(p.id);
        if (!at || tipped.has(p.id) || (!p.door && distance(me, at) > BUYER_MAP_RANGE)) continue;
        out.push({ x: at.x, z: at.z, kind: 'poi', glyph: 'T', pin: false, label: `${p.name} — ${p.door ? 'buy' : 'sell'}${p.door && p.buyer ? ' and sell' : ''}` });
      }
      const r = runner;
      if (r) {
        if (!r.hijacked) out.push({ x: r.van.pos.x, z: r.van.pos.z, kind: 'target', pin: true, label: 'The Vanes’ runner' });
        else for (const l of lockups) out.push({ x: l.pos.x, z: l.pos.z, kind: 'target', glyph: 'L', pin: false, label: `${l.name} — open the van here` });
      }
    }

    // ------------------------------------------------------------------ places

    function registerPlaces(): void {
      const layout = ctx.services.get('layout');
      const interactions = ctx.services.get('interactions');
      const player = ctx.services.get('player');
      const still = (): boolean => player.mode !== 'driving' || (player.vehicle?.speed ?? 0) < 3;
      for (const p of TRADE_PLACES) {
        const poi = layout.pois.find((q) => q.id === p.poi);
        if (!poi) continue;
        // A street buyer by a phone box stands a few strides along the pavement from it, so the box's own prompt
        // (a ringing job, the desk sergeant) keeps the box and the buyer keeps his spot.
        const box = poi.kind === 'phonebox';
        const at = box ? { x: poi.pos.x - Math.cos(poi.heading) * BOX_OFFSET, z: poi.pos.z + Math.sin(poi.heading) * BOX_OFFSET } : { x: poi.pos.x, z: poi.pos.z };
        pos.set(p.id, at);
        const what = p.door ? Object.keys(p.door).map((g) => GOOD_LABEL[g as TradeGood].name.toLowerCase()).join(', ') : '';
        offs.push(
          interactions.register({
            id: `trade:${p.id}`,
            pos: () => at,
            radius: box ? BOX_REACH : REACH,
            priority: 5,
            whileDriving: true,
            enabled: () => open() && ctx.state.mode === 'playing' && !card && still(),
            prompt: () => {
              const why = refusal();
              const head = p.wilf ? `Wilf’s arch — ${what}, a holdall` : p.door && p.buyer ? `Trade at ${p.name} — buy and sell` : p.door ? `Buy at ${p.name} — ${what}` : `Sell — ${p.name}`;
              return why ? `${head} (${why})` : head;
            },
            onInteract: () => {
              const why = refusal();
              if (why) ui()?.toast(`${p.name}: ${why}.`, 'warn', 2.6);
              else openCard(p.id);
            },
          }),
        );
      }
      for (const poi of layout.pois) {
        if (poi.kind !== 'lockup') continue;
        lockups.push({ id: poi.id, name: poi.name, pos: { x: poi.pos.x, z: poi.pos.z } });
      }
      const mine = layout.pois.find((q) => q.id === PLAYER_LOCKUP_ID);
      if (mine) {
        const at = { x: mine.pos.x, z: mine.pos.z };
        stashPos = at;
        offs.push(
          interactions.register({
            id: 'trade:stash',
            pos: () => at,
            radius: REACH + 1,
            priority: 4,
            whileDriving: true,
            enabled: () => open() && hasStash() && ctx.state.mode === 'playing' && !card && still() && !(runner?.hijacked && player.vehicle === runner.van),
            prompt: () => 'Your stash — put goods away or take them out',
            onInteract: () => openCard(STASH_ID),
          }),
        );
      }
      const inRunner = (): boolean => !!runner?.hijacked && player.mode === 'driving' && player.vehicle === runner.van;
      for (const l of lockups) {
        offs.push(
          interactions.register({
            id: `trade:open-van:${l.id}`,
            pos: () => l.pos,
            radius: LOCKUP_REACH,
            priority: 2,
            whileDriving: true,
            enabled: () => open() && inRunner() && (player.vehicle?.speed ?? 0) < 2,
            prompt: () => (ctx.state.heat > 0 ? 'Open up the Vanes’ van — lose the law first' : 'Open up the Vanes’ van'),
            onInteract: () => {
              if (ctx.state.heat > 0) ui()?.toast('Not with the law after you — lose them first.', 'warn', 2.6);
              else openRunner(l);
            },
          }),
        );
      }
    }

    function introNote(): void {
      if (!open()) return;
      const arch = pos.get('wilf');
      post({
        key: 'trade-intro',
        from: 'Wilf Tuckett',
        kind: 'story',
        subject: 'Wilf’s book',
        text: 'Mags says you’re all right, so here’s a little earner. Goods come in cheap at a few doors — mine at Arch 14, the night man at Bonded No. 3, Gorringe’s shed, the Lamp’s back door — and go out dear across town. Buy low, sell where they want it, and keep the big loads out of sight.\n\nI’ve started a page in your book for it: what you’re carrying, the prices you’ve heard, and Lenny’s tips. Pop round the arch.',
        ...(arch ? { action: { label: 'Mark on the map', mark: { x: arch.x, z: arch.z, label: 'Wilf — Arch 14' } } } : {}),
      });
    }

    return {
      id: 'trade',
      order: 540,
      init(c) {
        ctx = c;
        rng = c.rng.fork('trade');
        c.hooks.register('tradeState', () => {
          const t = now();
          const car = myCar();
          const vehicles = ctx.services.tryGet('vehicles');
          return {
            open: open(),
            now: +t.toFixed(1),
            pockets: { ...trader.s.pockets },
            stash: { ...trader.s.stash },
            boot: car ? { ...trader.boot(car)! } : null,
            boots: [...trader.boots].filter(([, b]) => !isEmpty(b)).map(([id, b]) => ({ id, goods: { ...b } })),
            paid: { ...trader.s.paid },
            holdall: holdall(),
            cap: { used: trader.capUsed(t), cap: TRADE.hourCap },
            stats: { ...trader.s.stats },
            tips: trader.liveTips(t).map((x) => ({ ...x, left: +(x.until - t).toFixed(1) })),
            card: card ? { place: card.place, info: cardInfo(card.place), rows: rowsFor(card.place).map((r) => ({ id: r.id, label: r.label, value: r.value, note: r.note, buttons: r.buttons })) } : null,
            runner: runner ? { van: runner.van.id, x: +runner.van.pos.x.toFixed(1), z: +runner.van.pos.z.toFixed(1), hijacked: runner.hijacked, leg: runner.leg, dwell: +runner.dwell.toFixed(1), alive: !!vehicles?.get(runner.van.id) } : null,
            places: TRADE_PLACES.map((p) => ({ id: p.id, poi: p.poi, ...(pos.get(p.id) ?? {}) })),
            carried: count(trader.s.pockets) + (car ? count(trader.boot(car)!) : 0),
          };
        });
        c.hooks.register('tradePress', (row, button) => {
          if (!card) return false;
          const r = rowsFor(card.place).find((x) => x.id === row);
          const b = r?.buttons.find((x) => x.id === button);
          if (!r || !b?.enabled) return false;
          const ok = press(card.place, row, button);
          card?.c.render();
          return ok;
        });
        c.hooks.register('tradeTip', (kind) => {
          if (!open()) return null;
          const t = now();
          trader.s.nextTip = t;
          if (kind === 'runner') trader.s.lastRunner = -1e9;
          // Retry until the kind asked for comes up (tests): the roll is the real one.
          for (let i = 0; i < 40; i++) {
            trader.s.nextTip = t;
            const tip = trader.nextTip(t, rng, runner === null);
            if (!tip) continue;
            if (!kind || tip.kind === kind) {
              if (tip.kind === 'runner' && !startRunner()) {
                trader.s.tips = trader.s.tips.filter((x) => x !== tip);
                return null;
              }
              persist();
              postTip(tip);
              return { ...tip };
            }
            trader.s.tips = trader.s.tips.filter((x) => x !== tip);
            if (tip.kind === 'runner') trader.s.lastRunner = -1e9;
          }
          return null;
        });
        c.hooks.register('tradeGive', (good, qty, paid) => {
          if (!TRADE_GOODS.includes(good as TradeGood) || !(qty > 0)) return false;
          trader.addHeld(good as TradeGood, Math.round(qty), (paid ?? 0) * Math.round(qty), trader.s.pockets);
          persist();
          return true;
        });
        offs.push(
          c.events.on('newGameStarted', () => {
            closeCard();
            if (runner) {
              const v = ctx.services.get('vehicles').get(runner.van.id);
              if (v && ctx.services.get('player').vehicle !== v) ctx.services.get('vehicles').despawn(v);
              runner = null;
            }
            load();
          }),
          c.events.on('playerBusted', () => {
            if (!open()) return;
            const lost = trader.seize('busted', myCar());
            reportLoss(lost, 'The law kept your goods', 'The law took everything you had on you:');
          }),
          c.events.on('playerWasted', () => {
            if (!open()) return;
            const lost = trader.seize('wasted', null);
            reportLoss(lost, 'Your pockets were emptied', 'While you were out cold somebody went through your pockets:');
          }),
          c.events.on('missionCompleted', (e) => {
            if (e.id === WET_PAPER && !e.replay) queueMicrotask(introNote);
          }),
        );
      },
      start() {
        load();
        registerPlaces();
        const b = book();
        if (b) offs.push(b.addSection('trade', { visible: open, page: bookPage, status: tileStatus }));
        const u = ui();
        if (u) offs.push(u.addMarkerSource(markers));
        introNote();
      },
      fixedUpdate(dt) {
        if (!open() || ctx.state.mode !== 'playing') return;
        trader.market.advance(dt);
        tickTips();
        tickRunner();
        check += dt;
        if (check >= 0.5) {
          check = 0;
          checkBoots();
        }
      },
      dispose() {
        closeCard();
        for (const off of offs) off();
        offs.length = 0;
      },
    };

    function tileStatus(): { status: string; alert?: boolean; badge?: string } {
      const t = now();
      const tips = trader.liveTips(t);
      if (runner?.hijacked) return { status: 'The Vanes’ van: get it to a lockup', alert: true };
      if (tips.length) return { status: `${tips.length} tip${tips.length > 1 ? 's' : ''} going · ${clock(Math.min(...tips.map((x) => x.until - t)))}`, alert: true, badge: String(tips.length) };
      const carried = count(trader.s.pockets);
      if (carried) return { status: `${carried} on you` };
      return { status: trader.s.stats.deals ? `${pounds(trader.s.stats.profit)} made so far` : 'Buy low, sell high' };
    }
  };

  return [driverSystem, tradeSystem];
}
